/**
 * src/app/modules/advance-applications/advance-applications.module.ts
 *
 * @author Bryan Henry <bryan@payfully.co>
 * @date 5/11/2021
 * @copyright Payfully, 2021
 */

import { CdkStepperModule } from '@angular/cdk/stepper';
import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSliderModule } from '@angular/material/slider';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';
import { GooglePlaceModule } from 'ngx-google-places-autocomplete-esb';
import { NgxMaskModule } from 'ngx-mask';
import { NgxPlaidLinkModule } from 'ngx-plaid-link';
import { NgxUploaderModule } from 'ngx-uploader-esb';

import { CommissionDetailsModule } from '../../modules/commission-details/commission-details.module';
import { ConfirmDepositDistributionsComponent } from '../../modules/commission-details/components/confirm-deposit-distributions/confirm-deposit-distributions.component';
import { CurrencyInputModule } from '../../modules/currency-input/currency-input.module';
import { DashboardWrapperComponent } from '../../modules/dashboard-wrapper/dashboard-wrapper.component';
import { DayInputModule } from '../../modules/day-input/day-input.module';
import { DealDetailsModule } from '../../modules/deal-details/deal-details.module';
import { DirectivesModule } from '../../modules/directives/directives.module';
import { InviteSignupProcessModule } from '../../modules/invite-signup-process/invite-signup-process.module';
import { MaterialModule } from '../../modules/material';
import { PipesModule } from '../../modules/pipes/pipes.module';
import { SelectDealForConfirmComponent } from '../../modules/select-deal-for-confirm/select-deal-for-confirm.component';
import { AgreementTextComponent } from '../../modules/shared/components/agreement-text/agreement-text.component';
import { SharedModule } from '../../modules/shared/shared.module';
import { AddNewBankAccountComponent } from './components/add-new-bank-account/add-new-bank-account.component';
import { AddNewBankAccountModalComponent } from './components/add-new-bank-account/add-new-bank-account-modal.component';
import { DashboardAdvanceBlockComponent } from './components/dashboard-advance-block/dashboard-advance-block.component';
import { DashboardAgentCommissionTransactionTotalComponent } from './components/dashboard-agent-commission-transaction-total/dashboard-agent-commission-transaction-total.component';
import { DashboardPaymentBlockComponent } from './components/dashboard-payment-block/dashboard-payment-block.component';
import { DashboardUpcomingDealsComponent } from './components/dashboard-upcoming-deals/dashboard-upcoming-deals.component';
import { EventCalendarHeaderComponent } from './components/event-calendar-header/event-calendar-header.component';
import { PaymentDetailsComponent } from './components/payment-details/payment-details.component';
import { RemoveSubstringPipe } from './components/schedule-payment/remove-substring.pipe';
import { SchedulePaymentComponent } from './components/schedule-payment/schedule-payment.component';
import { DashboardRoutingModule } from './dashboard-routing.module';
import { DashboardConnectBankAccountErrorComponent } from './pages/dashboard-banks/dashboard-connect-bank-account-error/dashboard-connect-bank-account-error.component';
import { DashboardNewBankAccountComponent } from './pages/dashboard-new-bank-account/dashboard-new-bank-account.component';
import { DashboardPayrollSettingsComponent } from './pages/dashboard-payroll-settings/dashboard-payroll-settings.component';
import {
  AfterXDaysPipe,
  AfterXDaysStringPipe,
} from './pipes/after-x-days.pipe';

@NgModule({
  declarations: [],
  exports: [DashboardPayrollSettingsComponent],
  imports: [
    CommonModule,
    CdkStepperModule,
    DashboardRoutingModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatRadioModule,
    MatIconModule,
    NgxMaskModule,
    GooglePlaceModule,
    MaterialModule,
    MatTabsModule,
    MatExpansionModule,
    MatGridListModule,
    MatTableModule,
    NgxUploaderModule,
    MatProgressSpinnerModule,
    NgxPlaidLinkModule,
    SharedModule,
    MatAutocompleteModule,
    RouterModule,
    MatToolbarModule,
    MatDialogModule,
    MatTooltipModule,
    MatSortModule,
    FormsModule,
    DayInputModule,
    DealDetailsModule,
    ReactiveFormsModule,
    MatProgressBarModule,
    MatButtonToggleModule,
    CurrencyInputModule,
    PipesModule,
    AgreementTextComponent,
    SelectDealForConfirmComponent,
    DashboardWrapperComponent,
    InviteSignupProcessModule,
    DirectivesModule,
    MatSliderModule,
    MatSlideToggleModule,
    CommissionDetailsModule,
    SchedulePaymentComponent,
    RemoveSubstringPipe,
    PaymentDetailsComponent,
    AddNewBankAccountComponent,
    AddNewBankAccountModalComponent,
    EventCalendarHeaderComponent,
    AfterXDaysPipe,
    AfterXDaysStringPipe,
    DashboardNewBankAccountComponent,
    DashboardAdvanceBlockComponent,
    DashboardPaymentBlockComponent,
    DashboardPayrollSettingsComponent,
    DashboardAgentCommissionTransactionTotalComponent,
    DashboardUpcomingDealsComponent,
    ConfirmDepositDistributionsComponent,
    DashboardConnectBankAccountErrorComponent,
  ],
})
export class DashboardModule {}
